import type { MatchState } from './state';
import type { SimConfig } from '../config/simConfig';
import type { EntityId, Vec2 } from '../types';
import { PITCH } from '../config/pitchConfig';
import { pickPassTarget } from './receiver';

// D112 (HB-C) — the lane-aware long ball.
//
// Today's THROUGH extrapolates blindly: `receiver.pos + receiver.vel × THROUGH_LEAD_SEC`, wherever that
// lands, into whoever happens to be standing there. `pickLongTarget` keeps the receiver CHOICE exactly as
// `pickPassTarget` makes it — aim-aligned, call-bonused, empty-cone fallback and all, so muscle memory is
// untouched — and moves only the POINT: it scores a fixed ladder of lead points along the receiver's
// projected run and takes the best. That is what turns R from "hoof it ahead of him" into "thread it
// between them".
//
// `laneMargin` is the worst-opponent time margin the bot brain has scored its own passes with since D57
// (it was `ai/utilityCarrier.laneSafety`'s inner math). It has to live HERE, inside the purity boundary,
// because `applyKicks` runs it for HUMANS — the bot brain now imports it from the sim rather than owning
// it, so a human's R and a bot's THROUGH are judging lanes by the same yardstick.
//
// PURE and deterministic: a fixed ladder, no rng draws, so the §7 normative order is untouched.

const OPP_CLOSE_SPEED = 8; // conservative defender closing speed for the margin (base MAX_SPEED)
const INTERCEPT_REACH = 0.9; // a defender "has" the lane once within claim-ish reach of it
const PERP_RELIEF = 0.06; // s of forgiveness per metre a defender sits OFF the lane (D57's calibration)

/**
 * Worst time margin over all opponents for the pass `from`→`to` at `meanSpeed`: for each opponent, the
 * time he needs to reach his closest point on the lane minus the time the ball passes it, plus a small
 * relief per metre he is off the lane (a defender METRES away is not committed to it, even when the raw
 * clock says he could make it). `Infinity` when nobody threatens the lane at all. Bigger is safer.
 */
export function laneMargin(state: MatchState, team: number, from: Vec2, to: Vec2, meanSpeed: number): number {
  const gx = to.x - from.x;
  const gy = to.y - from.y;
  const len = Math.hypot(gx, gy);
  if (len < 1e-6) return Infinity;
  const ux = gx / len;
  const uy = gy / len;
  let worst = Infinity;
  for (const o of state.players) {
    if (o.team === team || o.sentOff) continue;
    const rx = o.pos.x - from.x;
    const ry = o.pos.y - from.y;
    const raw = rx * ux + ry * uy;
    if (raw <= 0) continue; // behind the passer — no lane threat
    const along = Math.max(0.5, Math.min(len, raw));
    const perp = Math.abs(rx * uy - ry * ux);
    const tBall = along / Math.max(meanSpeed, 1);
    const tOpp = Math.max(0, perp - INTERCEPT_REACH) / OPP_CLOSE_SPEED;
    worst = Math.min(worst, tOpp - tBall + perp * PERP_RELIEF);
  }
  return worst;
}

/** The chosen receiver and the point the long ball is actually aimed at. */
export interface LongTarget {
  readonly id: EntityId;
  readonly point: Vec2;
}

export interface LongTargetOptions {
  /** HB-P4: this is a CROSS — in the final third, bias the ladder into the penalty box. */
  readonly box?: boolean;
}

/**
 * Receiver + lead point for a long ball from `origin` along `aimYaw`.
 *
 * The ladder walks `LONG.SAMPLES` rungs from the receiver's feet (0 s) out to `LONG.LEAD_MAX_SEC` along
 * his current run, scoring each by `laneMargin − DEEP_W × leadSeconds`. The overrun term is what stops a
 * clean-everywhere situation from flinging the deepest ball available: a deeper lead has to be genuinely
 * safer to earn it. Ties keep the shallower rung (strict `>`), which is the conservative choice.
 *
 * `box` (a cross, HB-P4): in the final third each rung is additionally pulled toward the penalty spot in
 * proportion to its own depth — the deep rungs of a wide cross end up in the box, which is where a cross
 * is meant to go, without inventing a blend constant. Outside the final third it is a no-op.
 */
export function pickLongTarget(
  state: MatchState,
  kicker: EntityId,
  origin: Vec2,
  aimYaw: number,
  speed: number,
  cfg: SimConfig,
  opts: LongTargetOptions = {},
): LongTarget | null {
  const soft = pickPassTarget(state, kicker, origin, aimYaw, speed, cfg);
  if (!soft) return null;
  const rp = state.players[soft.id];
  const kp = state.players[kicker];
  if (!rp || !kp) return null;

  const L = cfg.ai.LONG;
  const pitch = PITCH[state.mode.teamSize];
  const dir = state.attackDir[kp.team];
  const goalX = (dir * pitch.length) / 2;
  // HB-P4: the box target is the penalty spot — the classic cross destination.
  const boxSpot: Vec2 = { x: goalX - dir * pitch.penaltySpotDist, y: 0 };
  // attacking progress, 0 at our own goal line → 1 at theirs
  const attackX01 = (dir * kp.pos.x + pitch.length / 2) / pitch.length;
  const biasing = opts.box === true && attackX01 >= cfg.ai.CROSS.MIN_ATTACK_X;

  const samples = Math.max(1, Math.floor(L.SAMPLES));
  // The ladder's FLOOR is today's THROUGH lead, not the receiver's feet. The plan's ladder runs
  // 0 … LEAD_MAX_SEC, but `laneMargin` falls monotonically with lane length (a longer pass really is more
  // interceptable) and the overrun term docks depth as well — so on a clean pitch, where every rung's
  // margin is identical, rung 0 always wins and R degenerates into "pass to his feet", i.e. exactly
  // Space. That would delete the verb the wave exists to build. Starting at THROUGH_LEAD_SEC keeps R at
  // least as leading as it is today and spends the ladder on the actual feature — WHICH lead threads.
  const leadFloor = cfg.ai.THROUGH_LEAD_SEC;
  const leadSpan = Math.max(0, L.LEAD_MAX_SEC - leadFloor);
  let best: Vec2 = { x: rp.pos.x, y: rp.pos.y };
  let bestScore = -Infinity;
  for (let k = 0; k < samples; k++) {
    const frac = samples === 1 ? 0 : k / (samples - 1);
    const lead = leadFloor + frac * leadSpan;
    let px = rp.pos.x + rp.vel.x * lead;
    let py = rp.pos.y + rp.vel.y * lead;
    if (biasing) {
      // deeper rungs pull harder toward the box — the ladder fraction IS the blend, no new constant
      px += (boxSpot.x - px) * frac;
      py += (boxSpot.y - py) * frac;
    }
    const margin = laneMargin(state, kp.team, origin, { x: px, y: py }, speed);
    // Infinity (an untouched lane) is a perfectly good score, but it must still lose to the depth term
    // when several rungs are all untouched — cap it so the arithmetic stays finite.
    // DEPTH, signed by the verb: a THROUGH-ball must stay CATCHABLE, so leading deeper costs (that is
    // what stops a clean-everywhere pitch from flinging the longest ball on the ladder). A CROSS is the
    // opposite verb — hanging it deep INTO the box is the entire point — so the same weight rewards.
    const score = Math.min(margin, 4) + (biasing ? L.DEEP_W : -L.DEEP_W) * lead;
    if (score > bestScore) {
      bestScore = score;
      best = { x: px, y: py };
    }
  }
  return { id: soft.id, point: best };
}
